import config from '../_config/config.const';

export function isEmbeddedInIframe(): boolean {
    try {
        return window.self !== window.top;
    } catch {
        return true;
    }
}

// Get list of allowed target origins
const TARGET_ORIGINS: string[] = Array.isArray((config as any).ORIGINS) && (config as any).ORIGINS.length
    ? (config as any).ORIGINS
    : (config as any).ORIGIN
        ? [(config as any).ORIGIN]
        : [];

// Helper to post a message to all allowed parent origins
const postToAllowedParents = (message: unknown) => {
    if (!isEmbeddedInIframe()) return;

    if (!TARGET_ORIGINS.length) {
        window.parent.postMessage(message, '*');
        return;
    }

    TARGET_ORIGINS.forEach(origin => {
        try {
            window.parent.postMessage(message, origin);
        } catch (err) {
            console.warn(`[postMessage] failed for origin "${origin}":`, err);
        }
    });
};

let lastSentHeight = 0;
let pendingHeight = 0;
let postDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let retryGeneration = 0;

const STUB_HEIGHT = 280;
/** Keep all measure paths on the same padding or parent height will bounce (±50). */
const HEIGHT_PADDING = 50;
const HEIGHT_EPSILON = 8;
/** Longer than retry interval so intermediate measures coalesce into one RESIZE. */
const POST_DEBOUNCE_MS = 350;

function getEmbedContentHeight(): number {
    const formContainer = document.getElementById("formContainer");
    if (formContainer && formContainer.scrollHeight > 100) return formContainer.scrollHeight + HEIGHT_PADDING;

    const appScreen = document.getElementById("app-screen");
    if (appScreen) return Math.max(appScreen.scrollHeight, appScreen.offsetHeight);

    return document.body.scrollHeight;
}

function clearPostDebounce() {
    if (postDebounceTimer) {
        clearTimeout(postDebounceTimer);
        postDebounceTimer = null;
    }
}

function flushHeight(rounded: number) {
    clearPostDebounce();
    if (rounded < 1) return;
    if (Math.abs(rounded - lastSentHeight) < HEIGHT_EPSILON) return;
    lastSentHeight = rounded;
    postToAllowedParents({
        type: "RESIZE",
        height: rounded,
    });
}

function queueHeight(height: number) {
    const rounded = Math.ceil(height);
    if (rounded < 1) return;

    const formContainer = document.getElementById("formContainer");
    const hasRealForm = !!formContainer && formContainer.scrollHeight > 100;
    if (!hasRealForm && rounded < STUB_HEIGHT) return;

    // While height is held during a route/product transition, do not shrink
    // (avoids flicker). After releaseHeldIframeHeight, smaller heights must
    // be sent so the parent iframe can shrink (e.g. quotes → shorter form).
    const isHolding = document.documentElement.classList.contains("lq-hold-height");
    if (isHolding && lastSentHeight > 0 && rounded < lastSentHeight) return;

    pendingHeight = rounded;
    clearPostDebounce();
    postDebounceTimer = setTimeout(() => {
        postDebounceTimer = null;
        flushHeight(pendingHeight);
    }, POST_DEBOUNCE_MS);
}

function postHeight(height: number) {
    queueHeight(height);
}

export const sendHeightToParent = () => {
    postHeight(getEmbedContentHeight());
};

export const sendBodyHeightToParent = () => {
    postHeight(getEmbedContentHeight());
};

export const sendContainerHeightToParent = () => {
    const container = document.getElementById('formContainer');
    if (!container) return;
    postHeight(container.scrollHeight + HEIGHT_PADDING);
};

export const sendContainerHeightToParent2 = () => {
    const container = document.getElementById("formContainer");
    const containerHeight = container ? container.scrollHeight : 0;
    postHeight(Math.max(containerHeight, 0) + HEIGHT_PADDING);
};

export function sendScrollMessageToParent() {
    postToAllowedParents({
        type: "SCROLL_TOP",
        offset: 120,
    });
}

export function holdCurrentIframeHeight() {
    if (!isEmbeddedInIframe()) return;
    const px = Math.max(window.innerHeight || 0, lastSentHeight);
    if (px < 200) return;
    document.documentElement.style.setProperty("--lq-hold-height", `${px}px`);
    document.documentElement.classList.add("lq-hold-height");
}

export function releaseHeldIframeHeight() {
    document.documentElement.classList.remove("lq-hold-height");
    document.documentElement.style.removeProperty("--lq-hold-height");
    // Allow the next settled measure to shrink; drop any in-flight post so
    // intermediate retry values do not jump the parent iframe.
    lastSentHeight = 0;
    pendingHeight = 0;
    clearPostDebounce();
}

export function retryHeight(sendHeightFn: () => void, maxAttempts = 8, delay = 200) {
    window.scrollTo({ top: 0, behavior: "auto" });

    const generation = ++retryGeneration;
    let attempts = 0;
    let previousMeasure = -1;
    let stableCount = 0;

    const finish = () => {
        if (generation !== retryGeneration) return;
        if (pendingHeight > 0) flushHeight(pendingHeight);
    };

    const trySendingHeight = () => {
        if (generation !== retryGeneration) return;
        if (attempts >= maxAttempts) {
            finish();
            return;
        }
        attempts++;

        requestAnimationFrame(() => {
            setTimeout(() => {
                if (generation !== retryGeneration) return;

                const measured = Math.ceil(getEmbedContentHeight());
                sendHeightFn();

                if (previousMeasure > 0 && Math.abs(measured - previousMeasure) < HEIGHT_EPSILON) {
                    stableCount++;
                    if (stableCount >= 2) {
                        finish();
                        return;
                    }
                } else {
                    stableCount = 0;
                }
                previousMeasure = measured;

                if (attempts < maxAttempts) trySendingHeight();
                else finish();
            }, delay);
        });
    };

    trySendingHeight();
}
